import { useState } from "react";
import { format } from "date-fns";
import { HugeiconsIcon } from "@hugeicons/react";
import { Copy01Icon, Delete02Icon, Edit02Icon, PlusSignIcon } from "@hugeicons/core-free-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useRecvocTenants,
  useIssueRecvocTenant,
  useRevokeRecvocTenant,
  useUpdateRecvocTenant,
} from "../hooks/useRecvocTenants";
import type {
  IssueRecvocTenantResponse,
  RecvocTenantListItem,
} from "../types/recvocTenant.types";
import { toast } from "sonner";

interface RecvocTenantPanelProps {
  companyGuid: string;
}

export function RecvocTenantPanel({ companyGuid }: RecvocTenantPanelProps) {
  const { data, isLoading } = useRecvocTenants(companyGuid);
  const issueMutation = useIssueRecvocTenant();
  const revokeMutation = useRevokeRecvocTenant(companyGuid);
  const updateMutation = useUpdateRecvocTenant(companyGuid);

  const [issueOpen, setIssueOpen] = useState(false);
  const [issuedKey, setIssuedKey] = useState<IssueRecvocTenantResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RecvocTenantListItem | null>(null);
  const [editTarget, setEditTarget] = useState<RecvocTenantListItem | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const items = data?.items ?? [];

  const handleIssue = async () => {
    if (!name.trim()) {
      toast.error("이름을 입력하세요.");
      return;
    }
    const res = await issueMutation.mutateAsync({
      companyGuid,
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setIssuedKey(res);
    setIssueOpen(false);
    setName("");
    setDescription("");
  };

  const handleCopy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success("키를 복사했습니다.");
    } catch {
      toast.error("복사에 실패했습니다.");
    }
  };

  const openEdit = (t: RecvocTenantListItem) => {
    setEditTarget(t);
    setEditName(t.name);
    setEditDescription(t.description ?? "");
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    if (!editName.trim()) {
      toast.error("이름은 비울 수 없습니다.");
      return;
    }
    await updateMutation.mutateAsync({
      idx: editTarget.idx,
      req: {
        name: editName.trim(),
        description: editDescription,
      },
    });
    setEditTarget(null);
  };

  return (
    <div className="flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">RecVoc 연결 키</h4>
          <p className="text-xs text-gray-500">
            이 회사에 설치된 RecVoc 인스턴스마다 발급. 키 유출 시 폐기 후 재발급
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setIssueOpen(true)}
          className="h-7 bg-blue-600 px-2 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={14} className="mr-1" />
          신규 발급
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">이름</th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">키</th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">최근 신호</th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">발급일</th>
              <th className="h-10 px-3 text-right text-xs font-medium uppercase text-gray-500"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-400">로딩 중...</td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-400">
                  발급된 키가 없습니다. "신규 발급"으로 시작하세요.
                </td>
              </tr>
            )}
            {items.map((t) => (
              <tr key={t.idx} className="h-10 border-b last:border-b-0 hover:bg-gray-50">
                <td className="px-3 text-xs text-gray-700">{t.name}</td>
                <td className="px-3 font-mono text-xs text-gray-500">{t.keyMasked}</td>
                <td className="px-3 text-xs text-gray-500">
                  {t.lastSeenAt ? format(new Date(t.lastSeenAt), "MM-dd HH:mm") : "-"}
                </td>
                <td className="px-3 text-xs text-gray-500">
                  {t.registDate ? format(new Date(t.registDate), "yyyy-MM-dd") : "-"}
                </td>
                <td className="px-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(t)}
                      className="h-7 px-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      <HugeiconsIcon icon={Edit02Icon} size={14} className="mr-1" />
                      수정
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setRevokeTarget(t)}
                      className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <HugeiconsIcon icon={Delete02Icon} size={14} className="mr-1" />
                      폐기
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 신규 발급 다이얼로그 */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>RecVoc 키 신규 발급</DialogTitle>
            <DialogDescription>
              사이트/사무실 단위로 이름을 부여하세요. 발급된 키는 1회만 노출됩니다.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                이름 <span className="text-red-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="강남 사무실"
                maxLength={32}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">비고</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="관리자 메모 (선택)"
                rows={3}
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIssueOpen(false)}>
              취소
            </Button>
            <Button
              type="button"
              onClick={handleIssue}
              disabled={issueMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {issueMutation.isPending ? "발급 중..." : "발급"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 발급 결과 — 평문 키 1회 노출 */}
      <Dialog
        open={!!issuedKey}
        onOpenChange={(open) => {
          if (!open) setIssuedKey(null);
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>발급된 키</DialogTitle>
            <DialogDescription className="text-orange-600">
              ⚠ 이 키는 지금만 표시됩니다. 닫으면 더 이상 볼 수 없으니 RecVoc 설정에 바로 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 text-xs font-medium text-gray-600">{issuedKey?.name}</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-white px-2 py-1.5 text-xs">
                {issuedKey?.key}
              </code>
              <Button
                type="button"
                size="sm"
                onClick={() => issuedKey && handleCopy(issuedKey.key)}
                className="h-7"
              >
                <HugeiconsIcon icon={Copy01Icon} size={14} className="mr-1" />
                복사
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setIssuedKey(null)}>
              닫기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 수정 다이얼로그 */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>RecVoc 키 수정</DialogTitle>
            <DialogDescription>
              이름·URL·비고만 수정 가능. 키 값은 변경되지 않습니다.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                이름 <span className="text-red-500">*</span>
              </label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={32}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">비고</label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="text-xs"
              />
            </div>
            <div className="rounded-md bg-gray-50 px-2 py-1.5 font-mono text-[11px] text-gray-500">
              {editTarget?.keyMasked}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
              취소
            </Button>
            <Button
              type="button"
              onClick={handleSaveEdit}
              disabled={updateMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 폐기 확인 */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>키를 폐기하시겠습니까?</AlertDialogTitle>
            <AlertDialogDescription>
              "{revokeTarget?.name}" 키가 즉시 무효화됩니다. 해당 키로 동작 중이던 RecVoc은 webhook 401을 받게 됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (revokeTarget) {
                  await revokeMutation.mutateAsync(revokeTarget.idx);
                  setRevokeTarget(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              폐기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
